import { yupResolver } from '@hookform/resolvers/yup';
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { ActivityModel } from 'api-hooks/activity';
import { NavigationRoutes } from 'common/constants';
import { EnumList } from 'common/constants/enum';
import { ApiError, ApiResult } from 'common/repositories/common.model';
import { SeparatorVertical } from 'components/styles';
import useDestroyAction from 'hooks/use-destroy-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import Input from 'mantine-components/elements';
import {
  FullGridContainer,
  HalfGridContainer,
  OneThirdGridContainer,
} from 'mantine-components/container/grid-container';
import EnumSelectInput from 'modules/select-input/enum-select-input';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { UniskaiFormHeaderRef } from 'templates/form/uniskai-form-header';
import UniskaiFormTemplate from 'templates/form/uniskai-form-template';

import { ActivityFormSchema, ActivityFormType } from './form-type';
import useActivityDefaultValues from '../hooks/use-activity-default-values';

interface ActivityFormProps {
  query?: UseQueryResult<ApiResult<ActivityModel>, ApiError>;
  onSubmit: (
    input: ActivityFormType,
    form: UseFormReturn<ActivityFormType>,
  ) => Promise<ActivityModel>;
  saveMutation?: UseMutationResult;
}

export default function ActivityForm(props: ActivityFormProps) {
  const { t } = useTranslation();
  const activity = props.query?.data?.data;
  const defaultValues = useActivityDefaultValues(activity);
  const permission = useGetCrudPermission('activities');
  const { deleteMutate, onDelete } = useDestroyAction('activities');

  const titleRef = React.useRef<UniskaiFormHeaderRef>(null);
  React.useEffect(() => {
    let title = t(`pathname:${NavigationRoutes.ActivitiesCreate}`);
    if (activity) {
      title = t(`pathname:${NavigationRoutes.ActivitiesView}`, {
        extra: `${activity.code} ${activity.name}`,
      });
    }
    titleRef.current?.setTitle(title);
  }, [t, activity]);

  return (
    <UniskaiFormTemplate
      formProps={{
        defaultEditable: !activity,
        defaultValues,
        resolver: yupResolver(ActivityFormSchema()),
        onSubmit: props.onSubmit as any,
      }}
      customs={[
        {
          type: 'delete',
          loading: deleteMutate.isLoading,
          onClick: () => onDelete(activity?.id),
          show: permission.delete,
        },
        {
          type: 'edit',
          show: permission.update,
        },
      ]}
      data={activity}
      query={props.query}
      titleRef={titleRef}
      saveMutation={props.saveMutation}
    >
      <HalfGridContainer>
        <EnumSelectInput
          label={t('common:activity_type')}
          name="type"
          enumType={EnumList.ActivityType}
          required
        />
      </HalfGridContainer>
      <SeparatorVertical $margin={16} />
      <HalfGridContainer>
        <Input
          type="text"
          name="code"
          label={t('common:code')}
          placeholder={t('common:code')}
          required
        />
        <Input
          type="text"
          name="name"
          label={t('common:name')}
          placeholder={t('common:name')}
          required
        />
      </HalfGridContainer>
      <SeparatorVertical $margin={16} />
      <OneThirdGridContainer>
        <Input
          type="text"
          name="email"
          label={t('common:email')}
          placeholder={t('common:email')}
        />
        <Input
          type="text"
          name="phone"
          label={t('cruise:tel_phone')}
          placeholder={t('cruise:tel_phone')}
        />
        <Input
          type="text"
          name="phone_2"
          label={t('cruise:cellphone_number')}
          placeholder={t('cruise:cellphone_number')}
        />
      </OneThirdGridContainer>
      <SeparatorVertical $margin={16} />
      <FullGridContainer>
        <Input
          type="textarea"
          name="description"
          label={t('common:description')}
          placeholder={t('common:description')}
        />
      </FullGridContainer>
    </UniskaiFormTemplate>
  );
}
